import { generateClient } from "aws-amplify/api";
import { fetchAuthSession } from "aws-amplify/auth";
import { generatePresignedUrlForPut } from '@/graphql/mutations'
import sessionGraphqlMixin from '@/mixins/Organization/sessionGraphqlMixin'
import { S3_STORAGE_SCOPE } from '@/config/portalEnvironment'
import {
  buildPublicS3Url as buildScopedPublicS3Url,
  downloadS3ObjectBlob,
} from '@/utils/s3Storage'

const client = generateClient();

const STORAGE_READ_OPTIONS = {
  expiresIn: 3600,
  validateObjectExistence: false,
  useAccelerateEndpoint: false,
};

function resolveStorageScopeForKey(s3Key) {
  return isKnowledgeBaseS3Key(s3Key)
    ? S3_STORAGE_SCOPE.KNOWLEDGE_BASE
    : S3_STORAGE_SCOPE.GENERAL
}

const READ_FILE_TYPE_HINTS = ["GET", "get_object", "READ", "read", "download"];

const MIME_BY_EXT = {
  pdf: "application/pdf",
  txt: "text/plain",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  webp: "image/webp",
  svg: "image/svg+xml",
};

export function isOrganizationLogoS3Key(key) {
  return String(key || "").trim().startsWith("YANDLE-ORG-LOGS/");
}

export function isKnowledgeBaseS3Key(key) {
  const s = String(key || "").trim();
  if (!s) return false;
  if (s.startsWith("organizations/")) return true;
  const lower = s.toLowerCase();
  return lower.endsWith(".pdf") || lower.endsWith(".txt");
}

export function getKnowledgeBaseFileKind(key) {
  const lower = String(key || "").toLowerCase();
  if (lower.endsWith(".metadata.json")) return null;
  if (lower.endsWith(".pdf")) return "pdf";
  if (lower.endsWith(".txt")) return "text";
  return null;
}

export function isKnowledgeBaseMetadataKey(key) {
  return String(key || "").toLowerCase().endsWith(".metadata.json");
}

export function normalizeKnowledgeBaseS3Keys(value) {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value.map((key) => String(key || "").trim()).filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.map((key) => String(key || "").trim()).filter(Boolean);
        }
      } catch {
        // fall through to single key handling
      }
    }

    return [trimmed];
  }

  return [];
}

export function filterKnowledgeBasePreviewKeys(keys) {
  return normalizeKnowledgeBaseS3Keys(keys).filter(
    (key) => !isKnowledgeBaseMetadataKey(key) && !!getKnowledgeBaseFileKind(key)
  );
}

/** Human-readable filename from a knowledge-base S3 key (edit-org display). */
export function getKnowledgeBaseDisplayFilename(s3Key, orgId) {
  const parts = String(s3Key || "").split("/");
  const basename = parts[parts.length - 1] || String(s3Key || "").trim();
  if (!basename) return "";

  let name = basename;
  const id = String(orgId || "").trim();
  if (id && name.startsWith(`${id}-`)) {
    name = name.slice(id.length + 1);
  }

  // Backend-generated keys can prefix a millisecond timestamp; hide it.
  name = name.replace(/^\d{10,}-/, "");

  const overviewMatch = name.match(/^(.+)_overview(\.(pdf|txt))$/i);
  if (overviewMatch) {
    const stem = overviewMatch[1].replace(/_\d{10,}$/, "");
    name = `${stem}${overviewMatch[2]}`;
  }

  // Hide trailing timestamps and show underscores as spaces.
  return name
    .replace(/[_-]\d{10,}(?=\.[^.]+$)/, "")
    .replace(/_+/g, " ")
    .trim();
}

function inferMimeFromS3Key(key) {
  const lower = String(key || "").toLowerCase();
  if (lower.endsWith(".pdf")) return MIME_BY_EXT.pdf;
  if (lower.endsWith(".txt")) return MIME_BY_EXT.txt;

  const ext = lower.split(".").pop();
  return MIME_BY_EXT[ext] || "application/octet-stream";
}

export function getKnowledgeBasePreviewMime(kind) {
  if (kind === "pdf") return MIME_BY_EXT.pdf;
  if (kind === "text") return MIME_BY_EXT.txt;
  return "application/octet-stream";
}

function resolvePresignedUrl(response) {
  if (!response) return null;

  const candidates = [
    response.presigned_url,
    response.presignedUrl,
    response.url,
    response.download_url,
    response.downloadUrl,
    response.read_url,
    response.readUrl,
    response.data?.presigned_url,
    response.data?.presignedUrl,
    response.data?.url,
    response.data?.download_url,
    response.data?.downloadUrl,
    response.data?.read_url,
    response.data?.readUrl,
  ];

  const match = candidates.find((value) => typeof value === "string" && value.trim());
  return match?.trim() || null;
}

export function buildPublicS3Url(s3Key) {
  const scope = resolveStorageScopeForKey(s3Key)
  return buildScopedPublicS3Url(s3Key, scope)
}

async function toPreviewBlob(blob, s3Key, preferredMime) {
  const mime = preferredMime || inferMimeFromS3Key(s3Key);
  const buffer = await blob.arrayBuffer();
  return new Blob([buffer], { type: mime });
}

export async function createLocalFilePreviewUrl(file, kind) {
  const mime = getKnowledgeBasePreviewMime(kind);
  const buffer = await file.arrayBuffer();
  return URL.createObjectURL(new Blob([buffer], { type: mime }));
}

async function hasAmplifyStorageCredentials() {
  try {
    const session = await fetchAuthSession({ forceRefresh: true });
    return !!session.credentials?.accessKeyId;
  } catch {
    return false;
  }
}

export default {
  mixins: [sessionGraphqlMixin],
  methods: {
    async fetchPresignedReadUrlFromApi(s3Key, fileType) {
      const path = String(s3Key || '').trim()
      if (!path) return null

      try {
        const result = await client.graphql({
          query: generatePresignedUrlForPut,
          variables: {
            input: {
              s3_key: path,
              file_type: fileType,
            },
          },
        });
        const raw = result.data?.generatePresignedUrlForPut
        const parsed =
          typeof raw === 'string' ? JSON.parse(raw) : raw
        if (parsed?.status !== "SUCCESS") return null;
        return resolvePresignedUrl(parsed);
      } catch (error) {
        console.log("Err", error);
        return null;
      }
    },

    async getPresignedReadUrlViaGraphql(s3Key) {
      const path = String(s3Key || "").trim();
      if (!path) return null;

      for (const fileType of READ_FILE_TYPE_HINTS) {
        const url = await this.fetchPresignedReadUrlFromApi(path, fileType);
        if (url) return url;
      }

      return this.fetchPresignedReadUrlFromApi(path, inferMimeFromS3Key(path));
    },

    async fetchS3Blob(s3Key, scope = null) {
      const path = String(s3Key || "").trim();
      if (!path) return null;

      const storageScope = scope || resolveStorageScopeForKey(path);

      if (await hasAmplifyStorageCredentials()) {
        try {
          const blob = await downloadS3ObjectBlob(path, storageScope);
          if (blob?.size > 0) {
            return await toPreviewBlob(blob, path);
          }
        } catch (error) {
          console.log("Amplify downloadData failed, using URL fetch:", error);
        }
      }

      const readUrl =
        (await this.getPresignedReadUrlViaGraphql(path)) || buildPublicS3Url(path);
      const response = await fetch(readUrl, { cache: "no-store" });
      if (!response.ok) {
        throw new Error(`S3 fetch failed (${response.status})`);
      }
      const blob = await response.blob();
      return await toPreviewBlob(blob, path);
    },

    async fetchS3PreviewBlob(s3Key, kind, scope = null) {
      const path = String(s3Key || "").trim();
      if (!path) return null;

      const storageScope =
        scope ??
        (kind ? S3_STORAGE_SCOPE.KNOWLEDGE_BASE : resolveStorageScopeForKey(path));
      const blob = await this.fetchS3Blob(path, storageScope);
      if (!blob) return null;

      const mime = getKnowledgeBasePreviewMime(kind) || inferMimeFromS3Key(path);
      const buffer = await blob.arrayBuffer();
      return new Blob([buffer], { type: mime });
    },

    /**
     * Returns a blob: URL for in-app preview (img / iframe / object).
     * Caller must revoke with URL.revokeObjectURL when done.
     */
    async getS3ObjectUrl(key, kind = null, scope = null) {
      const path = String(key || "").trim();
      if (!path) return null;

      const storageScope =
        scope ??
        (kind ? S3_STORAGE_SCOPE.KNOWLEDGE_BASE : resolveStorageScopeForKey(path));

      try {
        const blob = kind
          ? await this.fetchS3PreviewBlob(path, kind, storageScope)
          : await this.fetchS3Blob(path, storageScope);
        if (!blob) return null;
        return URL.createObjectURL(blob);
      } catch (error) {
        console.log("Err", error);
        return null;
      }
    },

    async getS3TextContent(key, scope = S3_STORAGE_SCOPE.KNOWLEDGE_BASE) {
      const path = String(key || "").trim();
      if (!path) return "";

      try {
        const blob = await this.fetchS3PreviewBlob(path, "text", scope);
        if (!blob) return null;
        return await blob.text();
      } catch (error) {
        console.log("Err", error);
        return null;
      }
    },
  },
};
